import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    PaperPlaneTilt, ArrowLeft, DotsThreeVertical,
    BellSlash, Bell, Prohibit, Warning, Lock,
} from "@phosphor-icons/react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useMessages } from "../contexts/MessagesContext";
import { toast } from "sonner";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../components/ui/dropdown-menu";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";

const POLL_OPEN_MS = 10_000;     // thread open
const POLL_INBOX_MS = 15_000;    // inbox visible
// `"pro"` is the legacy single-tier value still on a chunk of users in
// the DB — the backend's subscription_tier_of() maps it to pro_global,
// so we must too or those users incorrectly see the upgrade prompt.
const PRO_TIERS = new Set(["pro", "pro_local", "pro_global"]);

function isPro(user) {
    return !!user && (user.role === "admin" || PRO_TIERS.has(user.subscription));
}

function timeAgo(iso) {
    if (!iso) return "";
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
}

function Avatar({ name, url, size = 40 }) {
    const initial = (name || "?").slice(0, 1).toUpperCase();
    if (url) {
        return (
            <img
                src={url}
                alt={name}
                className="rounded-full object-cover bg-muted"
                style={{ width: size, height: size }}
            />
        );
    }
    return (
        <div
            className="rounded-full bg-gradient-to-br from-[#6C63FF] to-[#FF8A00] grid place-items-center text-white font-bold"
            style={{ width: size, height: size, fontSize: size / 2.4 }}
        >
            {initial}
        </div>
    );
}

// ─────────────────────────── Inbox ───────────────────────────

function Inbox({ conversations, onPick, activeId, loading }) {
    if (loading) {
        return <div className="p-6 text-sm text-muted-foreground" data-testid="inbox-loading">Loading…</div>;
    }
    if (!conversations.length) {
        return (
            <div className="p-8 text-center" data-testid="inbox-empty">
                <PaperPlaneTilt size={32} weight="duotone" className="mx-auto text-muted-foreground mb-3" />
                <div className="text-sm font-semibold">No conversations yet</div>
                <div className="text-xs text-muted-foreground mt-1">
                    Open a post and use the ⋮ menu to message its author.
                </div>
            </div>
        );
    }
    return (
        <ul className="divide-y divide-border" data-testid="inbox-list">
            {conversations.map((c) => {
                const isActive = c.id === activeId;
                return (
                    <li key={c.id}>
                        <button
                            onClick={() => onPick(c)}
                            data-testid={`inbox-item-${c.id}`}
                            className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors
                                ${isActive ? "bg-[#6C63FF]/10" : "hover:bg-black/5 dark:hover:bg-white/5"}`}
                        >
                            <Avatar name={c.other_user?.name} url={c.other_user?.avatar_url} size={44} />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between gap-2">
                                    <div className="font-semibold text-sm truncate">
                                        {c.other_user?.name || "Unknown"}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground whitespace-nowrap">
                                        {timeAgo(c.last_message_at)}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 mt-0.5">
                                    <p className={`text-xs truncate flex-1 ${c.is_unread ? "text-foreground font-semibold" : "text-muted-foreground"}`}>
                                        {c.last_message_preview || "—"}
                                    </p>
                                    {c.is_unread && (
                                        <span
                                            data-testid={`inbox-unread-dot-${c.id}`}
                                            className="h-2 w-2 rounded-full bg-[#FF8A00] shrink-0"
                                        />
                                    )}
                                    {c.is_muted && (
                                        <BellSlash size={12} weight="duotone" className="text-muted-foreground shrink-0" />
                                    )}
                                </div>
                            </div>
                        </button>
                    </li>
                );
            })}
        </ul>
    );
}

// ─────────────────────────── Thread ───────────────────────────

function Thread({ conversationId, onClose, onRefreshInbox }) {
    const { user } = useAuth();
    const messagesCtx = useMessages();
    const [items, setItems] = useState([]);
    const [other, setOther] = useState(null);
    const [muted, setMuted] = useState(false);
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const bottomRef = useRef(null);

    const fetchThread = useCallback(async (markRead = true) => {
        try {
            const { data } = await api.get(`/conversations/${conversationId}/messages`);
            // Server returns newest first; reverse for chronological render.
            setItems([...data.items].reverse());
            setOther(data.other_user);
            setMuted(!!data.is_muted);
            if (markRead) {
                api.post(`/conversations/${conversationId}/read`)
                    .then(() => { messagesCtx.refresh(); onRefreshInbox?.(); })
                    .catch(() => {});
            }
        } catch (e) {
            toast.error("Couldn't load conversation");
        } finally {
            setLoading(false);
        }
    }, [conversationId, messagesCtx, onRefreshInbox]);

    useEffect(() => {
        setLoading(true);
        fetchThread(true);
        const t = setInterval(() => fetchThread(false), POLL_OPEN_MS);
        return () => clearInterval(t);
    }, [fetchThread]);

    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [items.length]);

    const send = async () => {
        const text = body.trim();
        if (!text || sending || !other) return;
        if (!isPro(user)) {
            toast.error("Sending messages requires a Pro subscription");
            return;
        }
        setSending(true);
        try {
            const { data } = await api.post("/messages", {
                recipient_id: other.id, body: text,
            });
            setItems((prev) => [...prev, data.message]);
            setBody("");
            onRefreshInbox?.();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Could not send";
            toast.error(typeof msg === "string" ? msg : "Could not send");
        } finally {
            setSending(false);
        }
    };

    const onKey = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    const toggleMute = async () => {
        try {
            if (muted) {
                await api.delete(`/conversations/${conversationId}/mute`);
                setMuted(false); toast.success("Notifications on");
            } else {
                await api.post(`/conversations/${conversationId}/mute`);
                setMuted(true); toast.success("Muted");
            }
        } catch { toast.error("Failed"); }
    };

    const block = async () => {
        if (!other || !window.confirm(`Block ${other.name}? Neither of you will be able to send new messages.`)) return;
        try {
            await api.post(`/users/${other.id}/block`);
            toast.success(`${other.name} blocked`);
            onClose?.();
            onRefreshInbox?.();
        } catch { toast.error("Failed"); }
    };

    const report = async () => {
        if (!other) return;
        const reason = window.prompt("Why are you reporting this user?");
        if (!reason || !reason.trim()) return;
        try {
            await api.post("/reports", {
                target_type: "user", target_id: other.id, reason: reason.trim(),
            });
            toast.success("Reported — admins will review");
        } catch { toast.error("Failed"); }
    };

    if (loading) {
        return <div className="p-8 text-sm text-muted-foreground" data-testid="thread-loading">Loading conversation…</div>;
    }

    return (
        <div className="flex flex-col h-full" data-testid="thread-view">
            {/* Thread header */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-card">
                <button
                    onClick={onClose}
                    data-testid="thread-back"
                    className="lg:hidden p-2 -ml-2 rounded-md hover:bg-black/5 dark:hover:bg-white/5"
                >
                    <ArrowLeft size={18} />
                </button>
                <Avatar name={other?.name} url={other?.avatar_url} size={36} />
                <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm truncate">{other?.name || "Unknown"}</div>
                    <div className="text-[11px] text-muted-foreground">
                        {PRO_TIERS.has(other?.subscription) || other?.role === "admin" ? "Pro member" : "Free member"}
                    </div>
                </div>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <button
                            className="p-2 rounded-md hover:bg-black/5 dark:hover:bg-white/5"
                            data-testid="thread-menu"
                        >
                            <DotsThreeVertical size={18} weight="bold" />
                        </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={toggleMute} data-testid="thread-mute-toggle">
                            {muted ? <><Bell size={14} className="mr-2" />Unmute</> : <><BellSlash size={14} className="mr-2" />Mute</>}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={block} data-testid="thread-block">
                            <Prohibit size={14} className="mr-2" />Block user
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={report} data-testid="thread-report">
                            <Warning size={14} className="mr-2" />Report user
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="thread-messages">
                {items.length === 0 ? (
                    <div className="text-center text-sm text-muted-foreground py-12">
                        No messages yet. Say hi.
                    </div>
                ) : items.map((m) => {
                    const mine = m.sender_id === user?.id;
                    return (
                        <div
                            key={m.id}
                            data-testid={`msg-${m.id}`}
                            className={`flex ${mine ? "justify-end" : "justify-start"}`}
                        >
                            <div className={`max-w-[78%] px-3 py-2 rounded-2xl text-sm leading-snug whitespace-pre-wrap break-words
                                ${mine
                                    ? "bg-[#6C63FF] text-white rounded-br-sm"
                                    : "bg-muted text-foreground rounded-bl-sm"}`}
                            >
                                {m.body}
                                <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-muted-foreground"}`}>
                                    {timeAgo(m.created_at)}
                                </div>
                            </div>
                        </div>
                    );
                })}
                <div ref={bottomRef} />
            </div>

            {/* Composer */}
            <div className="border-t border-border bg-card p-3">
                {!isPro(user) ? (
                    <div
                        className="flex items-center gap-3 rounded-xl border border-[#6C63FF]/30 bg-[#6C63FF]/5 px-3 py-2.5"
                        data-testid="thread-pro-cta"
                    >
                        <Lock size={18} weight="duotone" className="text-[#6C63FF]" />
                        <div className="text-xs flex-1">
                            <div className="font-semibold">Sending messages is a Pro feature</div>
                            <div className="text-muted-foreground">You can still read replies above.</div>
                        </div>
                        <a
                            href="/subscription"
                            className="px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-[#6C63FF] hover:bg-[#6C63FF]/90"
                            data-testid="thread-upgrade-link"
                        >
                            Upgrade
                        </a>
                    </div>
                ) : (
                    <div className="flex items-end gap-2">
                        <textarea
                            value={body}
                            onChange={(e) => setBody(e.target.value)}
                            onKeyDown={onKey}
                            placeholder="Write a message…"
                            rows={1}
                            maxLength={2000}
                            data-testid="thread-composer"
                            className="flex-1 resize-none rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#6C63FF]/40 max-h-32"
                        />
                        <button
                            onClick={send}
                            disabled={sending || !body.trim()}
                            data-testid="thread-send"
                            className="p-2.5 rounded-xl bg-[#6C63FF] text-white hover:bg-[#6C63FF]/90 disabled:opacity-50 transition-colors"
                        >
                            <PaperPlaneTilt size={18} weight="fill" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─────────────────────────── Page ───────────────────────────

export default function Messages() {
    const { conversationId: paramId } = useParams();
    const navigate = useNavigate();
    const [convs, setConvs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(paramId || null);

    const loadInbox = useCallback(async () => {
        try {
            const { data } = await api.get("/conversations?limit=50");
            setConvs(data.items || []);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        loadInbox();
        const t = setInterval(loadInbox, POLL_INBOX_MS);
        return () => clearInterval(t);
    }, [loadInbox]);

    useEffect(() => { setActive(paramId || null); }, [paramId]);

    const handlePick = (c) => {
        setActive(c.id);
        navigate(`/messages/${c.id}`);
    };

    const handleClose = () => {
        setActive(null);
        navigate("/messages");
    };

    return (
        <div className="max-w-6xl mx-auto" data-testid="messages-page">
            <div className="mb-4 flex items-center justify-between">
                <h1 className="font-display font-black text-2xl md:text-3xl tracking-tight">Messages</h1>
            </div>
            <div className="rounded-2xl border border-border bg-card overflow-hidden h-[calc(100vh-180px)] flex">
                {/* Inbox column */}
                <div className={`${active ? "hidden lg:flex" : "flex"} flex-col w-full lg:w-[340px] border-r border-border`}>
                    <Inbox conversations={convs} onPick={handlePick} activeId={active} loading={loading} />
                </div>
                {/* Thread column */}
                <div className={`${active ? "flex" : "hidden lg:flex"} flex-col flex-1 min-w-0`}>
                    {active ? (
                        <Thread
                            conversationId={active}
                            onClose={handleClose}
                            onRefreshInbox={loadInbox}
                        />
                    ) : (
                        <div className="flex-1 grid place-items-center text-sm text-muted-foreground" data-testid="thread-empty">
                            Pick a conversation to start reading
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────── Pro upgrade modal (exported helper) ───────────────────────────

export function ProRequiredModal({ open, onOpenChange, action = "send messages" }) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="pro-required-modal" className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Lock size={20} weight="duotone" className="text-[#6C63FF]" />
                        Pro feature
                    </DialogTitle>
                    <DialogDescription>
                        You need a Pro subscription to {action}. Upgrade to unlock direct messages with any user.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="pro-modal-cancel">
                        Not now
                    </Button>
                    <Button
                        onClick={() => { window.location.href = "/subscription"; }}
                        className="bg-[#6C63FF] hover:bg-[#6C63FF]/90 text-white"
                        data-testid="pro-modal-upgrade"
                    >
                        Upgrade to Pro
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
